import { useQuery } from "@apollo/react-hooks";
import gql from "graphql-tag";
import { isUndefined, omitBy } from "lodash";
import { useSearchForTaskContexts } from "core/queries/SearchForTaskContexts";
import { SearchForTaskSubjectsItem } from "core/queries/SearchForTaskSubjects";
import {
  GetQueryParamBuilding_building as GetQueryParamBuildingDataGenerated,
  GetQueryParamBuilding as GetQueryParamBuildingGenerated,
  GetQueryParamBuildingVariables as GetQueryParamBuildingVariablesGenerated,
} from "core/queries/__generated__/GetQueryParamBuilding";
import {
  GetQueryParamInquiry_inquiry as GetQueryParamInquiryDataGenerated,
  GetQueryParamInquiry as GetQueryParamInquiryGenerated,
  GetQueryParamInquiryVariables as GetQueryParamInquiryVariablesGenerated,
} from "core/queries/__generated__/GetQueryParamInquiry";
import {
  GetQueryParamLeaseInquiry_leaseInquiry as GetQueryParamLeaseInquiryDataGenerated,
  GetQueryParamLeaseInquiry as GetQueryParamLeaseInquiryGenerated,
  GetQueryParamLeaseInquiryVariables as GetQueryParamLeaseInquiryVariablesGenerated,
} from "core/queries/__generated__/GetQueryParamLeaseInquiry";
import {
  GetQueryParamSpace_cufSpace as GetQueryParamSpaceDataGenerated,
  GetQueryParamSpace as GetQueryParamSpaceGenerated,
  GetQueryParamSpaceVariables as GetQueryParamSpaceVariablesGenerated,
} from "core/queries/__generated__/GetQueryParamSpace";
import {
  GetQueryParamUnit_unit as GetQueryParamUnitDataGenerated,
  GetQueryParamUnit as GetQueryParamUnitGenerated,
  GetQueryParamUnitVariables as GetQueryParamUnitVariablesGenerated,
} from "core/queries/__generated__/GetQueryParamUnit";
import { CreateTaskFormPropsInput } from "tasks/actions";
import { formatAccountName, parseGraphQLId } from "tasks/helpers";
import { subjectTypesConfig } from "tasks/hooks/useCreateTaskFormConfig";
import { TaskContextSubjectTypeEnum } from "typings/globalGQLTypes";

// Queries
export interface GetQueryParamBuildingData
  extends GetQueryParamBuildingDataGenerated {}
export interface GetQueryParamBuildingResponse
  extends GetQueryParamBuildingGenerated {}
export interface GetQueryParamBuildingVariables
  extends GetQueryParamBuildingVariablesGenerated {}
export const getQueryParamBuilding = gql`
  query GetQueryParamBuilding($id: ID!) {
    building(id: $id) {
      buildingId: id
      name
      city {
        id
      }
      fullAddress
      unitCount
    }
  }
`;
export interface GetQueryParamInquiryData
  extends GetQueryParamInquiryDataGenerated {}
export interface GetQueryParamInquiryResponse
  extends GetQueryParamInquiryGenerated {}
export interface GetQueryParamInquiryVariables
  extends GetQueryParamInquiryVariablesGenerated {}
export const getQueryParamInquiry = gql`
  query GetQueryParamInquiry($id: ID!) {
    inquiry: booking(id: $id) {
      id
      account {
        id
        firstName
        lastName
      }
      checkInTime
      checkOutTime
      intrastayCleaningConfig {
        id
        availableTimes
      }
      unit {
        id
        city {
          id
        }
        shortInternalRef
      }
    }
  }
`;

export interface GetQueryParamLeaseInquiryData
  extends GetQueryParamLeaseInquiryDataGenerated {}
export interface GetQueryParamLeaseInquiryResponse
  extends GetQueryParamLeaseInquiryGenerated {}
export interface GetQueryParamLeaseInquiryVariables
  extends GetQueryParamLeaseInquiryVariablesGenerated {}
export const getQueryParamLeaseInquiry = gql`
  query GetQueryParamLeaseInquiry($id: ID!) {
    leaseInquiry(id: $id) {
      id
      account {
        id
        firstName
        lastName
      }
      checkInTime
      checkOutTime
      intrastayCleaningConfig {
        id
        availableTimes
      }
      unit {
        id
        city {
          id
        }
        shortInternalRef
      }
    }
  }
`;

export interface GetQueryParamSpaceData
  extends GetQueryParamSpaceDataGenerated {}
export interface GetQueryParamSpaceResponse
  extends GetQueryParamSpaceGenerated {}
export interface GetQueryParamSpaceVariables
  extends GetQueryParamSpaceVariablesGenerated {}
export const getQueryParamSpace = gql`
  query GetQueryParamSpace($id: ID!) {
    cufSpace(id: $id) {
      id
      internalRef
      type
    }
  }
`;

export interface GetQueryParamUnitData extends GetQueryParamUnitDataGenerated {}
export interface GetQueryParamUnitResponse extends GetQueryParamUnitGenerated {}
export interface GetQueryParamUnitVariables
  extends GetQueryParamUnitVariablesGenerated {}
export const getQueryParamUnit = gql`
  query GetQueryParamUnit($id: ID!) {
    unit(id: $id) {
      id
      city {
        id
      }
      images(limit: 0) {
        id
        url
      }
      shortInternalRef
    }
  }
`;

export interface UseGetQueryParamBuildingOptions {
  variables: GetQueryParamBuildingVariables;
  skip?: boolean;
}
export const useGetQueryParamBuildingQuery = ({
  variables,
  skip = false,
}: UseGetQueryParamBuildingOptions) =>
  useQuery<GetQueryParamBuildingResponse, GetQueryParamBuildingVariables>(
    getQueryParamBuilding,
    {
      fetchPolicy: "cache-and-network",
      notifyOnNetworkStatusChange: true,
      skip,
      variables,
    },
  );

// Hooks
export interface UseGetQueryParamInquiryOptions {
  variables: GetQueryParamInquiryVariables;
  skip?: boolean;
}
export const useGetQueryParamInquiryQuery = ({
  variables,
  skip = false,
}: UseGetQueryParamInquiryOptions) =>
  useQuery<GetQueryParamInquiryResponse, GetQueryParamInquiryVariables>(
    getQueryParamInquiry,
    {
      fetchPolicy: "cache-and-network",
      notifyOnNetworkStatusChange: true,
      skip,
      variables,
    },
  );
export interface UseGetQueryParamLeaseInquiryOptions {
  variables: GetQueryParamLeaseInquiryVariables;
  skip?: boolean;
}
export const useGetQueryParamLeaseInquiryQuery = ({
  variables,
  skip = false,
}: UseGetQueryParamLeaseInquiryOptions) =>
  useQuery<
    GetQueryParamLeaseInquiryResponse,
    GetQueryParamLeaseInquiryVariables
  >(getQueryParamLeaseInquiry, {
    fetchPolicy: "cache-and-network",
    notifyOnNetworkStatusChange: true,
    skip,
    variables,
  });

export interface UseGetQueryParamSpaceOptions {
  variables: GetQueryParamSpaceVariables;
  skip?: boolean;
}
export const useGetQueryParamSpaceQuery = ({
  variables,
  skip = false,
}: UseGetQueryParamSpaceOptions) =>
  useQuery<GetQueryParamSpaceResponse, GetQueryParamSpaceVariables>(
    getQueryParamSpace,
    {
      fetchPolicy: "cache-and-network",
      notifyOnNetworkStatusChange: true,
      skip,
      variables,
    },
  );

export interface UseGetQueryParamUnitOptions {
  variables: GetQueryParamUnitVariables;
  skip?: boolean;
}
export const useGetQueryParamUnitQuery = ({
  variables,
  skip = false,
}: UseGetQueryParamUnitOptions) =>
  useQuery<GetQueryParamUnitResponse, GetQueryParamUnitVariables>(
    getQueryParamUnit,
    {
      fetchPolicy: "cache-and-network",
      notifyOnNetworkStatusChange: true,
      skip,
      variables,
    },
  );

export const convertDataToSubject = (
  data:
    | GetQueryParamBuildingResponse
    | GetQueryParamInquiryResponse
    | GetQueryParamLeaseInquiryResponse
    | GetQueryParamSpaceResponse
    | GetQueryParamUnitResponse
    | undefined,
) => {
  if (!data) return null;

  const subject =
    ("building" in data && {
      id: `BuildingSearchFeedItem|${parseGraphQLId(data.building.buildingId)}`,
      title: data.building.name,
      subtitle: "",
      resource: data.building,
      __typename: "SearchFeedItem",
    }) ||
    ("inquiry" in data && {
      id: `InquirySearchFeedItem|${parseGraphQLId(data.inquiry.id)}`,
      title: formatAccountName(data.inquiry.account),
      subtitle: "",
      resource: data.inquiry,
      __typename: "SearchFeedItem",
    }) ||
    ("leaseInquiry" in data && {
      id: `LeaseInquirySearchFeedItem|${parseGraphQLId(data.leaseInquiry.id)}`,
      title: formatAccountName(data.leaseInquiry.account),
      subtitle: "",
      resource: data.leaseInquiry,
      __typename: "SearchFeedItem",
    }) ||
    ("cufSpace" in data && {
      id: `CufSpaceSearchFeedItem|${parseGraphQLId(data.cufSpace.id)}`,
      title: data.cufSpace.internalRef,
      subtitle: data.cufSpace.id,
      resource: data.cufSpace,
      __typename: "SearchFeedItem",
    }) ||
    ("unit" in data && {
      id: `UnitSearchFeedItem|${parseGraphQLId(data.unit.id)}`,
      title: data.unit.shortInternalRef,
      subtitle: "",
      resource: data.unit,
      __typename: "SearchFeedItem",
    });
  return subject ? (subject as SearchForTaskSubjectsItem) : null;
};
export interface useGetQueryParamSubjectProps
  extends CreateTaskFormPropsInput {}

export const useGetQueryParamSubject = (
  props: useGetQueryParamSubjectProps,
) => {
  // fromEntries is barking, ignoring for now.
  // @ts-ignore-next-line
  const urlParams = Object.fromEntries(
    new URLSearchParams(window.location.search),
  );
  const { subjectId, subjectType, taskContextName, taskType } = Object.assign(
    urlParams,
    omitBy(props, isUndefined),
  );

  const subjectTypeConfig = subjectTypesConfig[subjectType];
  const locationPlaces = subjectTypeConfig?.locationPlaces || [];

  // These queries are skipped if the subjectType does not match.
  const {
    loading: buildingLoading,
    error: buildingError,
    data: buildingData,
  } = useGetQueryParamBuildingQuery({
    skip: !subjectId || subjectType !== TaskContextSubjectTypeEnum.Building,
    variables: {
      id: subjectId,
    },
  });
  const {
    loading: inquiryLoading,
    error: inquiryError,
    data: inquiryData,
  } = useGetQueryParamInquiryQuery({
    skip: !subjectId || subjectType !== TaskContextSubjectTypeEnum.Inquiry,
    variables: {
      id: subjectId,
    },
  });
  const {
    loading: leaseInquiryLoading,
    error: leaseInquiryError,
    data: leaseInquiryData,
  } = useGetQueryParamLeaseInquiryQuery({
    skip: !subjectId || subjectType !== TaskContextSubjectTypeEnum.LeaseInquiry,
    variables: {
      id: subjectId,
    },
  });
  const {
    loading: spaceLoading,
    error: spaceError,
    data: spaceData,
  } = useGetQueryParamSpaceQuery({
    skip: !subjectId || subjectType !== TaskContextSubjectTypeEnum.Cuf_Space,
    variables: {
      id: subjectId,
    },
  });
  const {
    loading: unitLoading,
    error: unitError,
    data: unitData,
  } = useGetQueryParamUnitQuery({
    skip: !subjectId || subjectType !== TaskContextSubjectTypeEnum.Unit,
    variables: {
      id: subjectId,
    },
  });
  const {
    data: contextData,
    loading: contextLoading,
    error: contextError,
  } = useSearchForTaskContexts({
    variables: {
      searchTerm: taskContextName,
      filters: {
        taskContext: {
          taskTypes: [taskType],
          taskLocationPlaces: locationPlaces,
          subjectTypes: [subjectType],
        },
      },
      pagination: {
        cursor: "0",
        limit: 1,
      },
    },
    skip: !taskContextName || !taskType,
  });

  const context = contextData?.searchWithMetadata.items[0] || null;
  // This maps the output to a format that `searchWithMetadata` returns.
  const subject = convertDataToSubject(
    buildingData || inquiryData || leaseInquiryData || spaceData || unitData,
  );
  return {
    // This will only be returned on load success.
    context,
    subject,
    // These will be returned regardless of loading, success or error.
    error:
      buildingError ||
      contextError ||
      inquiryError ||
      leaseInquiryError ||
      spaceError ||
      unitError,
    loading:
      buildingLoading ||
      contextLoading ||
      inquiryLoading ||
      leaseInquiryLoading ||
      spaceLoading ||
      unitLoading,
    subjectId,
    subjectType,
    taskType,
  };
};
